import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { BRANCHES } from 'src/app/@core/constants/branch-constants';
import {
  DatatableColumn,
  DatatableColumnType,
  SearchControl,
  SearchControlType,
  StringMap,
} from 'src/app/@theme/datatable/datatable.component';
import { RankListResponseWrapper } from 'src/app/api/models';

export interface RanklistRow {
  rank: string;
  team: { picture: string; teamName: string };
  time?: string;
  month: string;
  room: string;
  level: string;
}

@Component({
  selector: 'app-responsiveranklist',
  templateUrl: './responsiveranklist.component.html',
  styleUrls: [
    './responsiveranklist.component.scss',
    '../../../../ngx-datatable-dark.scss',
    '../../../../ngx-datatable-light.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ResponsiveranklistComponent {
  ColumnMode = ColumnMode;
  ranklist: RankListResponseWrapper[] = [];
  ranklistRows: RanklistRow[] = [];
  searchControlSettings: SearchControl[] = [];
  rooms: StringMap = {};
  roomId?: string;
  namespace?: string;

  readonly levels = {
    EASY: 'EASY',
    MEDIUM: 'MEDIUM',
    HARD: 'HARD',
    X_HARD: 'MASTER',
    ONLINE: 'ONLINE',
  };

  @Input() loading: boolean = false;
  @Input() filters: StringMap | undefined;
  @Input() theme: string = '';

  @Input() branches: StringMap | undefined;
  @Input() namespaces: StringMap | undefined;

  @Input('namespace') set setNamespace(namespace: string | undefined) {
    if (namespace) {
      this.namespace = BRANCHES[namespace].toUpperCase();
    }
  }

  @Input('roomId') set setRoomId(roomId: string | undefined) {
    this.roomId = roomId;
  }

  @Output() filterChange = new EventEmitter<any>();

  @HostBinding('class.light-theme') get isLightTheme() {
    return this.theme === 'light';
  }
  @HostBinding('class.dark-theme') get isDarkTheme() {
    return this.theme === 'dark';
  }

  columns: DatatableColumn[] = [
    {
      prop: 'rank',
      name: '',
      sortable: false,
      minWidth: 80,
      width: 125,
      maxWidth: 125,
      cellClass: 'responsiveTextLarge',
    },
    {
      prop: 'team',
      name: '',
      sortable: false,
      type: DatatableColumnType.ARRAY,
      minWidth: 175,
      maxWidth: 320,
      width: 210,
      cellClass: 'responsiveText',
    },
    {
      prop: 'time',
      name: '',
      sortable: false,
      type: DatatableColumnType.TIME,
      minWidth: 85,
      width: 125,
      maxWidth: 125,
      cellClass: 'responsiveTextLarge',
    } as DatatableColumn,
  ];

  private buildSearchControlSettings(): void {
    if (!this.ranklist) {
      return;
    }

    const today = new Date();
    const originalToday = new Date(today);
    const months: StringMap = {};

    for (let i = 0; i < 4; i++) {
      const dateKey =
        today.getFullYear() +
        '.' +
        String(today.getMonth() + 1).padStart(2, '0') +
        '.';

      const dateValue =
        today.getFullYear().toString() +
        '.' +
        (today.getMonth() + 1).toString().padStart(2, '0') +
        '.';

      months[dateKey] = dateValue;
      today.setMonth(today.getMonth() - 1);
    }

    const defaultMonth =
      originalToday.getFullYear() +
      '.' +
      String(originalToday.getMonth() + 1).padStart(2, '0') +
      '.';

    this.searchControlSettings = [
      {
        title: 'Month',
        type: SearchControlType.SELECT,
        property: 'month',
        options: months,
        defaultValue: defaultMonth,
      },
      {
        title: 'Room',
        type: SearchControlType.SELECT,
        property: 'room',
        options: this.rooms,
        defaultValue: this.roomId,
      },
      {
        title: 'Level',
        type: SearchControlType.SELECT,
        property: 'level',
        options: this.levels,
        defaultValue: 'MEDIUM',
      },
    ];
  }

  @Input('rooms')
  set setRooms(rooms: StringMap) {
    this.rooms = rooms;
    this.buildSearchControlSettings();
    this.mapRanklistRows(this.ranklist);
  }

  @Input('ranklist')
  set setRanklist(ranklist: RankListResponseWrapper[]) {
    this.ranklist = ranklist;
    this.buildSearchControlSettings();
    this.mapRanklistRows(ranklist);
  }

  private mapRanklistRows(ranklist: RankListResponseWrapper[]): void {
    if (ranklist) {
      const data = ranklist.map((games) => games.data);

      this.ranklistRows = data.flatMap((items) => {
        return items!.map((item) => {
          return {
            rank: item.rank ? '#' + item.rank.toString() : '',
            team: item.picture
              ? { picture: item.picture, team: item.teamName?.toUpperCase() }
              : {
                  picture: '../../../../assets/noteampicture.png',
                  team: item.teamName?.toUpperCase(),
                },
            time: item.time ? item.time : '',
            month: item.month,
            room: Object.keys(this.rooms).find(
              (key) => this.rooms[key] === item.room?.toUpperCase(),
            ),
            level: item.level,
          } as unknown as RanklistRow;
        });
      });
    }
  }

  onFilterChange(event: any): void {
    this.filterChange.emit(event);
  }
}
